/**
 * API Retry Utility with Exponential Backoff
 * Handles network errors and transient failures in concurrent booking scenarios
 */

class ApiRetryError extends Error {
  constructor(message, attempts, lastError) {
    super(message);
    this.name = 'ApiRetryError';
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

/**
 * Retry configuration defaults
 */
const DEFAULT_RETRY_CONFIG = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  jitterRange: 0.1, // 10% jitter
  retryableErrors: [
    'NetworkError',
    'TimeoutError',
    'ECONNRESET',
    'ETIMEDOUT',
    'ENOTFOUND',
    'ESOCKETTIMEDOUT'
  ],
  retryableStatusCodes: [
    408, // Request Timeout
    429, // Too Many Requests
    500, // Internal Server Error
    502, // Bad Gateway
    503, // Service Unavailable
    504  // Gateway Timeout
  ]
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt, config) {
  const exponentialDelay = Math.min(
    config.initialDelay * Math.pow(config.backoffMultiplier, attempt - 1),
    config.maxDelay
  );
  
  // Add jitter to prevent thundering herd
  const jitter = exponentialDelay * config.jitterRange * (Math.random() - 0.5) * 2;
  return Math.max(0, exponentialDelay + jitter);
}

/**
 * Check if error is retryable
 */
function isRetryableError(error, config) {
  // Check network errors
  if (error.code && config.retryableErrors.includes(error.code)) {
    return true;
  }
  
  // Check status codes
  if (error.response?.status && config.retryableStatusCodes.includes(error.response.status)) {
    return true;
  }
  
  // Check for specific error messages
  if (error.message) {
    const message = error.message.toLowerCase();
    if (message.includes('network') || 
        message.includes('timeout') || 
        message.includes('fetch failed')) {
      return true;
    }
  }
  
  // Don't retry client errors (4xx except specific ones)
  if (error.response?.status >= 400 && error.response?.status < 500) {
    return !config.retryableStatusCodes.includes(error.response.status);
  }
  
  return false;
}

/**
 * Sleep utility
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute API call with retry logic
 * @param {Function} apiCall - The API call function to execute
 * @param {Object} config - Retry configuration
 * @param {Function} onRetry - Optional callback for retry attempts
 */
export async function withRetry(apiCall, config = {}, onRetry = null) {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError = null;
  
  for (let attempt = 1; attempt <= retryConfig.maxAttempts; attempt++) {
    try {
      // Execute the API call
      const result = await apiCall();
      
      // Success - return result
      if (attempt > 1) {
        console.log(`✅ API call succeeded after ${attempt} attempts`);
      }
      return result;
      
    } catch (error) {
      lastError = error;
      
      // Check if we should retry
      const shouldRetry = isRetryableError(error, retryConfig) && attempt < retryConfig.maxAttempts;
      
      if (shouldRetry) {
        const delay = calculateDelay(attempt, retryConfig);
        
        console.warn(`⚠️ API call failed (attempt ${attempt}/${retryConfig.maxAttempts}):`, {
          error: error.message,
          status: error.response?.status,
          retryingIn: `${delay}ms`
        });
        
        // Call retry callback if provided
        if (onRetry) {
          onRetry(attempt, delay, error);
        }
        
        // Wait before retrying
        await sleep(delay);
        
      } else {
        // Max attempts reached or non-retryable error
        throw new ApiRetryError(
          `API call failed after ${attempt} attempt(s): ${error.message}`,
          attempt,
          error
        );
      }
    }
  }
  
  // Should never reach here, but just in case
  throw new ApiRetryError(
    `API call failed after ${retryConfig.maxAttempts} attempts`,
    retryConfig.maxAttempts,
    lastError
  );
}

/**
 * Circuit breaker implementation for API calls
 */
export class CircuitBreaker {
  constructor(config = {}) {
    this.config = {
      failureThreshold: 5,
      resetTimeout: 60000, // 1 minute
      halfOpenRequests: 3,
      ...config
    };
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
  }
  
  async execute(apiCall) {
    // Check if circuit should be reset
    if (this.state === 'OPEN' && 
        Date.now() - this.lastFailureTime > this.config.resetTimeout) {
      this.state = 'HALF_OPEN';
      this.successCount = 0;
      console.log('🔄 Circuit breaker entering HALF_OPEN state');
    }
    
    // If circuit is open, fail fast
    if (this.state === 'OPEN') {
      throw new Error('Circuit breaker is OPEN - service unavailable');
    }
    
    try {
      const result = await apiCall();
      
      // Record success
      if (this.state === 'HALF_OPEN') {
        this.successCount++;
        if (this.successCount >= this.config.halfOpenRequests) {
          this.state = 'CLOSED';
          this.failureCount = 0;
          console.log('✅ Circuit breaker CLOSED - service recovered');
        }
      } else {
        this.failureCount = 0;
      }
      
      return result;
      
    } catch (error) {
      // Record failure
      this.failureCount++;
      this.lastFailureTime = Date.now();
      
      if (this.failureCount >= this.config.failureThreshold) {
        this.state = 'OPEN';
        console.error('🔴 Circuit breaker OPEN - too many failures');
      }
      
      throw error;
    }
  }
}

/**
 * Batch queue for API calls to prevent overwhelming the server
 */
export class BatchQueue {
  constructor(config = {}) {
    this.config = {
      batchSize: 5,
      batchDelay: 100,
      ...config
    };
    
    this.queue = [];
    this.processing = false;
  }
  
  async add(apiCall) {
    return new Promise((resolve, reject) => {
      this.queue.push({ apiCall, resolve, reject });
      
      if (!this.processing) {
        this.process();
      }
    });
  }
  
  async process() {
    if (this.processing || this.queue.length === 0) {
      return;
    }
    
    this.processing = true;
    
    while (this.queue.length > 0) {
      // Take a batch
      const batch = this.queue.splice(0, this.config.batchSize);
      
      // Process batch concurrently
      const promises = batch.map(({ apiCall, resolve, reject }) =>
        apiCall()
          .then(resolve)
          .catch(reject)
      );
      
      await Promise.allSettled(promises);
      
      // Wait before processing next batch
      if (this.queue.length > 0) {
        await sleep(this.config.batchDelay);
      }
    }
    
    this.processing = false;
  }
}

/**
 * Create a resilient API client with retry, circuit breaker, and batching
 */
export function createResilientApiClient(config = {}) {
  const circuitBreaker = new CircuitBreaker(config.circuitBreaker);
  const batchQueue = new BatchQueue(config.batchQueue);
  
  return {
    async execute(apiCall, retryConfig = {}) {
      // Wrap API call with circuit breaker
      const wrappedCall = () => circuitBreaker.execute(apiCall);
      
      // Add retry logic
      return withRetry(wrappedCall, retryConfig);
    },
    
    async batch(apiCall) {
      // Add to batch queue
      return batchQueue.add(apiCall);
    },
    
    getCircuitState() {
      return circuitBreaker.state;
    },
    
    resetCircuit() {
      circuitBreaker.state = 'CLOSED';
      circuitBreaker.failureCount = 0;
    }
  };
}

export default {
  withRetry,
  CircuitBreaker,
  BatchQueue,
  createResilientApiClient
};